import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { CustomAssetManager } from '../lib/CustomAssetManager'
import { convorcherAdmin } from '../lib/convorcherAdmin'
import { findPrefabRow, asPhraseList, samePhraseList } from '../lib/prefabReflection'
import { readGestureFields } from '../lib/gestureFields'
import PrefabTriggeringSection, { PrefabSectionHeader } from './PrefabTriggeringSection'
import DirtyBadge from './DirtyBadge'

const ACCENT = '#8b6cff'
const BG = '#16171a'
const BG2 = '#1a1b1f'
const BG3 = '#1c1d22'
const LINE = '#ffffff0e'
const FG = '#ececef'
const FG2 = '#a8aab2'
const FG3 = '#6e7079'
const GREEN = '#4caf82'
const AMBER = '#e09b3d'
const RED = '#e05858'

// ── Helpers ──────────────────────────────────────────────────────────────────

function statusColor(status) {
  if (status === 'ready') return GREEN
  if (status === 'invalid') return RED
  return AMBER
}

function testGesture(gesture) {
  window.__EVENT_RUNTIME__?.fireEvent('onAnimation', { name: gesture.tag })
}

function testPrefab(prefab) {
  // Use the same playPrefab path the graph node uses, bypassing the legacy
  // onPrefabTrigger event chain in conversational.json (which only handles
  // hardcoded `prefab_${name}` clips, not DB-backed custom prefabs).
  // Returns the playback duration in seconds, or null when the runtime
  // isn't up or the tag doesn't resolve — callers surface that loudly.
  return window.__EVENT_RUNTIME__?.playPrefabByTag?.(prefab.tag) ?? null
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function SectionHeader({ label, count, onAdd }) {
  return (
    <div style={{ padding: '6px 10px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${LINE}` }}>
      <span style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, letterSpacing: '.14em', color: FG3, textTransform: 'uppercase' }}>
        {label} {count > 0 && <span>({count})</span>}
      </span>
      {onAdd && (
        <button onClick={onAdd} style={{ background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>+</button>
      )}
    </div>
  )
}

function ListItem({ label, sub, selected, onClick, badge, badgeColor, onAction, actionLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderLeft: `2px solid ${selected ? ACCENT : 'transparent'}` }}>
      <button onClick={onClick} style={{
        flex: 1, textAlign: 'left', padding: '6px 8px 6px 8px',
        background: selected ? ACCENT + '18' : 'transparent',
        border: 'none', color: selected ? FG : FG2, fontSize: 11, fontFamily: 'var(--m-font)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap', overflow: 'hidden', minWidth: 0,
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{label}</span>
        {sub && <span style={{ fontSize: 9, color: FG3, flexShrink: 0 }}>{sub}</span>}
        {badge && <span style={{ fontSize: 9, color: badgeColor ?? FG3, flexShrink: 0 }}>{badge}</span>}
      </button>
      {onAction && (
        <button onClick={e => { e.stopPropagation(); onAction() }} style={{
          flexShrink: 0, background: 'none', border: 'none', color: FG3,
          cursor: 'pointer', padding: '4px 6px', fontSize: 12,
        }} title={actionLabel}>{actionLabel ?? '▶'}</button>
      )}
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 9.5, fontFamily: 'var(--m-font)', color: FG3, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder }) {
  return (
    <input
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`,
        borderRadius: 4, padding: '5px 8px', color: FG, fontSize: 11, fontFamily: 'var(--m-font)',
      }}
    />
  )
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      style={{
        width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`,
        borderRadius: 4, padding: '5px 8px', color: value ? FG : FG3, fontSize: 11, fontFamily: 'var(--m-font)',
      }}
    >
      <option value="">{placeholder ?? '— none —'}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  )
}

function Btn({ children, onClick, primary, danger, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: disabled ? 'default' : 'pointer',
      background: primary ? ACCENT : danger ? RED + '22' : 'transparent',
      border: `1px solid ${primary ? ACCENT : danger ? RED : '#ffffff18'}`,
      color: primary ? '#fff' : danger ? RED : FG2,
      opacity: disabled ? 0.4 : 1,
    }}>{children}</button>
  )
}

// ── Detail panels ─────────────────────────────────────────────────────────────

// The gesture row is read through readGestureFields, NOT field by field: the
// gestures endpoint emits the two asset ids in camelCase only, and reading
// them as `body_animation_id` left both dropdowns blank on every existing
// gesture — which is what "Save Gesture wipes my settings" looked like.
// See src/lib/gestureFields.js.
export function GestureDetail({ gesture, animAssets, onSave, onDelete }) {
  const [form, setForm] = useState(() => readGestureFields(gesture))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  useEffect(() => {
    setForm(readGestureFields(gesture))
    setError(null); setDeleteError(null)
  }, [gesture?.id])

  const bodyOptions = animAssets.filter(a => a.kind === 'body').map(a => ({ value: a.id, label: a.name }))
  const faceOptions = animAssets.filter(a => a.kind === 'face').map(a => ({ value: a.id, label: a.name }))

  const doSave = async () => {
    if (!form.tag) { setError('Tag is required'); return }
    setSaving(true); setError(null)
    try {
      await onSave(gesture?.id, form)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    try {
      await onDelete(gesture.id)
    } catch (e) {
      setDeleteError(e.message)
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: ACCENT, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          {gesture?.id ? 'Edit Gesture' : 'New Gesture'}
        </div>
        {gesture?.id && (
          <button onClick={() => testGesture(gesture)} style={{
            background: ACCENT + '22', border: `1px solid ${ACCENT}44`, borderRadius: 4,
            color: ACCENT, fontSize: 11, fontFamily: 'var(--m-font)', cursor: 'pointer', padding: '3px 10px',
          }}>▶ Test</button>
        )}
      </div>
      <Field label="Tag"><Input value={form.tag} onChange={v => setForm(f => ({ ...f, tag: v }))} placeholder="e.g. laugh" /></Field>
      <Field label="Display Name"><Input value={form.displayName} onChange={v => setForm(f => ({ ...f, displayName: v }))} placeholder="e.g. Laugh" /></Field>
      <Field label="Body Animation">
        <Select value={form.bodyAnimationId} onChange={v => setForm(f => ({ ...f, bodyAnimationId: v }))} options={bodyOptions} placeholder="— none —" />
      </Field>
      <Field label="Face Animation">
        <Select value={form.faceAnimationId} onChange={v => setForm(f => ({ ...f, faceAnimationId: v }))} options={faceOptions} placeholder="— none —" />
      </Field>
      <div style={{ display: 'flex', gap: 10, marginBottom: 0 }}>
        <div style={{ flex: 1 }}>
          <Field label="Duration (ms)">
            <input type="number" value={form.durationMs} onChange={e => setForm(f => ({ ...f, durationMs: Number(e.target.value) }))}
              style={{ width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`, borderRadius: 4, padding: '5px 8px', color: FG, fontSize: 11, fontFamily: 'var(--m-mono)' }} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Body Weight">
            <input type="number" step="0.05" min="0" max="1" value={form.bodyWeight} onChange={e => setForm(f => ({ ...f, bodyWeight: Number(e.target.value) }))}
              style={{ width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`, borderRadius: 4, padding: '5px 8px', color: FG, fontSize: 11, fontFamily: 'var(--m-mono)' }} />
          </Field>
        </div>
      </div>
      {error && <div style={{ fontSize: 10, color: RED, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{error}</div>}
      {deleteError && <div style={{ fontSize: 10, color: RED, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{deleteError}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn primary onClick={doSave} disabled={saving}>{saving ? 'Saving…' : 'Save Gesture'}</Btn>
        {gesture?.id && <Btn danger onClick={doDelete}>Delete</Btn>}
      </div>
    </div>
  )
}

// Read a prefab record into the editor's form shape. The asset-manager API
// returns id/blend fields in camelCase only (audioAssetId, bodyAnimationId,
// blendInMs…) while a few fields also carry snake_case aliases (body_clip_name,
// duration_ms…). Accept BOTH so a saved audio clip / body anim isn't silently
// dropped on reopen. Also enforce the EITHER-asset-id-OR-stock-clip-name
// invariant (prefer the asset id) in case a record has both set.
function readPrefabFields(p) {
  const bodyAnimationId = p?.body_animation_id ?? p?.bodyAnimationId ?? null
  const faceAnimationId = p?.face_animation_id ?? p?.faceAnimationId ?? null
  return {
    tag:             p?.tag ?? '',
    displayName:     p?.display_name ?? p?.displayName ?? '',
    bodyAnimationId,
    faceAnimationId,
    bodyClipName:    bodyAnimationId ? null : (p?.body_clip_name ?? p?.bodyClipName ?? null),
    faceClipName:    faceAnimationId ? null : (p?.face_clip_name ?? p?.faceClipName ?? null),
    audioAssetId:    p?.audio_asset_id ?? p?.audioAssetId ?? null,
    durationMs:      p?.duration_ms ?? p?.durationMs ?? null,
    blendInMs:       p?.blend_in_ms ?? p?.blendInMs ?? 180,
    blendOutMs:      p?.blend_out_ms ?? p?.blendOutMs ?? 220,
    loop:            p?.loop ?? false,
    text:            p?.text ?? '',
    status:          p?.status ?? 'ready',
  }
}

export function PrefabDetail({ prefab, animAssets, audioAssets, onSave, onDelete, initialTag = null }) {
  // Body/face slot accepts EITHER a custom asset (animation_assets FK) OR a
  // stock-clip name (the clip is already loaded by AvatarModel from the
  // static manifest). One of bodyAnimationId / bodyClipName is populated,
  // never both. Same for face.
  //
  // `initialTag` prefills the tag on a NEW prefab only (the Prefabs tab's
  // "create prefab for this orphan trigger" shortcut) — an existing record's
  // fields always come from the record.
  const seedForm = () => ({ ...readPrefabFields(prefab), ...(!prefab && initialTag ? { tag: initialTag } : {}) })
  const [form, setForm] = useState(seedForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  // Test-button feedback (ENG-874 follow-up): the click was silent — playback
  // is invisible when the avatar sits behind a full-screen window (the
  // Conversation workspace), and a failed tag lookup only warned in the
  // console. 'playing' holds for the clip duration; a null duration is a loud
  // inline error, never a no-op.
  const [testing, setTesting] = useState(false)
  const [testError, setTestError] = useState(null)
  const testTimer = useRef(null)
  useEffect(() => () => clearTimeout(testTimer.current), [])

  const doTest = () => {
    setTestError(null)
    const durationS = testPrefab(prefab)
    if (durationS == null) {
      setTestError(`Couldn't play "${prefab.tag}" — the runtime doesn't know this prefab (is the avatar loaded?)`)
      return
    }
    setTesting(true)
    clearTimeout(testTimer.current)
    testTimer.current = setTimeout(() => setTesting(false), durationS * 1000)
  }

  // Triggering half (ENG-843): lives on the convorcher phrase-trigger row
  // keyed (character slug, tag), NOT on the AAM record. Loaded on open;
  // status is 'loading' | 'ready' | 'error'. A load failure is LOUD (error
  // box + Retry) — never a silent empty list the author might then "save"
  // over the panel's real data (phrases are omitted from the save while
  // unloaded, so nothing can be clobbered).
  const [trig, setTrig] = useState({ status: 'loading', base: { phrases: [], notFor: [] }, error: null })
  const [phrases, setPhrases] = useState([])
  const [notFor, setNotFor] = useState([])
  const [trigRetry, setTrigRetry] = useState(0)

  useEffect(() => {
    setForm(seedForm())
    setError(null); setDeleteError(null)
    setTesting(false); setTestError(null); clearTimeout(testTimer.current)
  }, [prefab?.id, initialTag])

  useEffect(() => {
    let cancelled = false
    // For a NEW prefab created from an orphan convorcher trigger, initialTag
    // names an EXISTING trigger row — load it so its phrases carry over and
    // "attach an animation to this trigger" round-trips instead of silently
    // authoring a second, empty phrase set over the row's real one.
    const tag = prefab?.tag ?? initialTag
    if (!tag) {
      // Brand-new prefab with no trigger heritage: author from empty.
      setTrig({ status: 'ready', base: { phrases: [], notFor: [] }, error: null })
      setPhrases([]); setNotFor([])
      return
    }
    setTrig({ status: 'loading', base: { phrases: [], notFor: [] }, error: null })
    convorcherAdmin.listPrefabs()
      .then((rows) => {
        if (cancelled) return
        const row = findPrefabRow(rows, CustomAssetManager.getCharacterId(), tag)
        const base = { phrases: asPhraseList(row?.phrases), notFor: asPhraseList(row?.notFor) }
        setTrig({ status: 'ready', base, error: null })
        setPhrases(base.phrases); setNotFor(base.notFor)
      })
      .catch((err) => {
        if (cancelled) return
        setTrig({ status: 'error', base: { phrases: [], notFor: [] }, error: err })
      })
    return () => { cancelled = true }
  }, [prefab?.id, prefab?.tag, initialTag, trigRetry])

  // Stock clips are loaded by AvatarModel at startup from the static
  // manifest. Read them lazily on each render — cheap, and ensures newly-
  // loaded clips after a character switch get picked up.
  const stockBodyNames = useMemo(() => {
    const actions = (typeof window !== 'undefined' && window.__AVATAR_ACTIONS__) || {}
    return Object.keys(actions).filter(n => !n.startsWith('custom_')).sort()
  }, [animAssets]) // refresh when custom set changes (proxy for "avatar refreshed")

  const stockFaceNames = useMemo(() => {
    const player = typeof window !== 'undefined' ? window.__FACE_CLIP_PLAYER__ : null
    const clips = player?._clips || {}
    return Object.keys(clips).filter(n => !n.startsWith('custom_')).sort()
  }, [animAssets])

  // Build merged options. Stock items prefixed with "stock:" so the value
  // encodes both the source and the lookup key. Custom items prefixed
  // "asset:". Empty value clears.
  const bodyOptions = useMemo(() => [
    ...animAssets.filter(a => a.kind === 'body').map(a => ({ value: `asset:${a.id}`, label: `• ${a.name}` })),
    ...stockBodyNames.map(n => ({ value: `stock:${n}`, label: `◦ ${n}` })),
  ], [animAssets, stockBodyNames])

  const faceOptions = useMemo(() => [
    ...animAssets.filter(a => a.kind === 'face').map(a => ({ value: `asset:${a.id}`, label: `• ${a.name}` })),
    ...stockFaceNames.map(n => ({ value: `stock:${n}`, label: `◦ ${n}` })),
  ], [animAssets, stockFaceNames])

  const audioOptions = audioAssets.map(a => ({ value: a.id, label: a.name }))

  // Compute the Select's current value from whichever field is set.
  const bodySelectValue = form.bodyAnimationId ? `asset:${form.bodyAnimationId}` : form.bodyClipName ? `stock:${form.bodyClipName}` : ''
  const faceSelectValue = form.faceAnimationId ? `asset:${form.faceAnimationId}` : form.faceClipName ? `stock:${form.faceClipName}` : ''

  const setBodySelection = (raw) => {
    if (!raw) return setForm(f => ({ ...f, bodyAnimationId: null, bodyClipName: null }))
    const [kind, key] = raw.split(':', 2)
    if (kind === 'asset') return setForm(f => ({ ...f, bodyAnimationId: key, bodyClipName: null }))
    if (kind === 'stock') return setForm(f => ({ ...f, bodyAnimationId: null, bodyClipName: key }))
  }

  const setFaceSelection = (raw) => {
    if (!raw) return setForm(f => ({ ...f, faceAnimationId: null, faceClipName: null }))
    const [kind, key] = raw.split(':', 2)
    if (kind === 'asset') return setForm(f => ({ ...f, faceAnimationId: key, faceClipName: null }))
    if (kind === 'stock') return setForm(f => ({ ...f, faceAnimationId: null, faceClipName: key }))
  }

  // Compute dirty state against the record's normalized fields (same reader as
  // the initial form), so an unchanged record reads as clean regardless of the
  // API's camel/snake casing. body/face are EITHER an asset id OR a stock clip
  // name, never both.
  const base = readPrefabFields(prefab)
  const dirty = !!prefab && (
    form.tag             !== base.tag ||
    form.displayName     !== base.displayName ||
    form.bodyAnimationId !== base.bodyAnimationId ||
    form.faceAnimationId !== base.faceAnimationId ||
    form.bodyClipName    !== base.bodyClipName ||
    form.faceClipName    !== base.faceClipName ||
    form.audioAssetId    !== base.audioAssetId ||
    form.blendInMs       !== base.blendInMs ||
    form.blendOutMs      !== base.blendOutMs ||
    form.loop            !== base.loop ||
    form.text            !== base.text
  )
  // Triggering dirty: only meaningful once the convorcher row loaded ('ready')
  // — while loading/errored the chips aren't editable, so they can't drift.
  const trigDirty = trig.status === 'ready' && (
    !samePhraseList(phrases, trig.base.phrases) ||
    !samePhraseList(notFor, trig.base.notFor)
  )

  // For a brand-new prefab (no record yet) the form is "dirty" once the
  // tag field has content — that's the minimum required to save.
  const dirtyForNew = !prefab && form.tag.length > 0
  const anyDirty = dirty || dirtyForNew || trigDirty
  const badgeState = error ? 'error' : saving ? 'saving' : anyDirty ? 'dirty' : 'clean'

  const doSave = async () => {
    if (!form.tag) { setError('Tag is required'); return }
    // Status is always 'ready' — the editor no longer exposes a draft state.
    // Drafts existed only as a DB column; nothing in the app consumed them.
    // phrases/notFor ride along ONLY when the convorcher row actually loaded —
    // an unloaded (error) state must never clobber panel-authored triggers
    // (undefined = preserve, see prefabReflection.planUpsert).
    const payload = {
      ...form,
      status: 'ready',
      ...(trig.status === 'ready' ? { phrases, notFor } : {}),
    }
    setSaving(true); setError(null)
    try {
      await onSave(prefab?.id, payload)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const doDelete = async () => {
    try {
      await onDelete(prefab.id)
    } catch (e) {
      setDeleteError(e.message)
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: ACCENT, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          {prefab?.id ? 'Edit Prefab' : 'New Prefab'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {prefab?.id && (
            <button
              onClick={doTest}
              disabled={dirty || testing}
              title={dirty ? 'Save to test' : testing ? 'Playing on the avatar' : 'Play prefab'}
              style={{
                background: testing ? GREEN + '22' : ACCENT + '22',
                border: `1px solid ${testing ? GREEN + '44' : ACCENT + '44'}`, borderRadius: 4,
                color: testing ? GREEN : ACCENT, fontSize: 11, fontFamily: 'var(--m-font)',
                cursor: dirty || testing ? 'default' : 'pointer', padding: '3px 10px',
                opacity: dirty ? 0.4 : 1,
              }}
            >{testing ? '▶ Playing…' : '▶ Test'}</button>
          )}
        </div>
      </div>
      {testError && (
        <div data-testid="prefab-test-error" style={{ fontSize: 10, color: RED, margin: '-6px 0 10px', fontFamily: 'var(--m-font)' }}>
          {testError}
        </div>
      )}

      <PrefabSectionHeader first name="Playback" note={{ prefix: 'stored in', target: 'AAM' }} />

      <Field label="Tag"><Input value={form.tag} onChange={v => setForm(f => ({ ...f, tag: v }))} placeholder="e.g. wave_hello" /></Field>
      <Field label="Display Name"><Input value={form.displayName} onChange={v => setForm(f => ({ ...f, displayName: v }))} placeholder="e.g. Wave Hello" /></Field>

      <Field label="Body Animation (• custom · ◦ stock)">
        <Select value={bodySelectValue} onChange={setBodySelection} options={bodyOptions} placeholder="— none —" />
      </Field>
      <Field label="Face Animation (• custom · ◦ stock)">
        <Select value={faceSelectValue} onChange={setFaceSelection} options={faceOptions} placeholder="— none —" />
      </Field>
      <Field label="Audio">
        <Select value={form.audioAssetId} onChange={v => setForm(f => ({ ...f, audioAssetId: v }))} options={audioOptions} placeholder="— none —" />
      </Field>

      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Field label="Blend In (ms)">
            <input type="number" value={form.blendInMs} onChange={e => setForm(f => ({ ...f, blendInMs: Number(e.target.value) }))}
              style={{ width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`, borderRadius: 4, padding: '5px 8px', color: FG, fontSize: 11, fontFamily: 'var(--m-mono)' }} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Blend Out (ms)">
            <input type="number" value={form.blendOutMs} onChange={e => setForm(f => ({ ...f, blendOutMs: Number(e.target.value) }))}
              style={{ width: '100%', boxSizing: 'border-box', background: BG, border: `1px solid ${LINE}`, borderRadius: 4, padding: '5px 8px', color: FG, fontSize: 11, fontFamily: 'var(--m-mono)' }} />
          </Field>
        </div>
      </div>

      <label style={{ fontSize: 11, fontFamily: 'var(--m-font)', color: FG2, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <input type="checkbox" checked={!!form.loop} onChange={e => setForm(f => ({ ...f, loop: e.target.checked }))} />
        Loop
      </label>

      <Field label="Text (spoken)">
        <Input value={form.text} onChange={v => setForm(f => ({ ...f, text: v }))} placeholder="optional spoken text" />
      </Field>

      <PrefabSectionHeader name="Triggering" note={{ prefix: 'synced to', target: 'convorcher' }} />
      {trig.status === 'loading' && (
        <div data-testid="trigger-loading" style={{
          display: 'flex', alignItems: 'center', gap: 8,
          border: '1px dashed rgba(255,255,255,.14)', borderRadius: 6,
          padding: '10px 12px', margin: '4px 0 12px',
          fontSize: 11, fontFamily: 'var(--m-font)', color: FG2,
        }}>
          <DirtyBadge state="saving" message="Loading…" />
          Loading trigger phrases from convorcher…
        </div>
      )}
      {trig.status === 'error' && (
        <div data-testid="trigger-load-error" style={{
          fontSize: 10, color: RED, fontFamily: 'var(--m-font)', margin: '4px 0 12px',
          border: `1px solid ${RED}55`, borderRadius: 4, padding: '6px 8px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ flex: 1 }}>
            Couldn't load trigger phrases from convorcher: {String(trig.error?.message ?? trig.error)}.
            Trigger edits are disabled — saving won't touch them.
          </span>
          <Btn onClick={() => setTrigRetry(n => n + 1)}>Retry</Btn>
        </div>
      )}
      {trig.status === 'ready' && (
        <div style={{ marginBottom: 12 }}>
          <PrefabTriggeringSection
            tag={form.tag}
            phrases={phrases}
            notFor={notFor}
            onPhrasesChange={setPhrases}
            onNotForChange={setNotFor}
          />
        </div>
      )}

      {error && <div style={{ fontSize: 10, color: RED, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{error}</div>}
      {deleteError && <div style={{ fontSize: 10, color: RED, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{deleteError}</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <Btn primary onClick={doSave} disabled={saving || !form.tag || !anyDirty}>
          {saving ? 'Saving…' : 'Save'}
        </Btn>
        <DirtyBadge state={badgeState} message={error ?? undefined} />
        {prefab?.id && <Btn danger onClick={doDelete}>Delete</Btn>}
      </div>
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function PrefabEditorPanel() {
  const [animAssets, setAnimAssets] = useState([])
  const [audioAssets, setAudioAssets] = useState([])
  const [gestures, setGestures] = useState([])
  const [prefabs, setPrefabs] = useState([])
  const [selected, setSelected] = useState(null) // { kind, data }

  const refresh = useCallback(() => {
    setAnimAssets(CustomAssetManager.listAnimationAssets())
    setAudioAssets(CustomAssetManager.listAudioAssets())
    setGestures(CustomAssetManager.listGestures())
    setPrefabs(CustomAssetManager.listPrefabs())
  }, [])

  useEffect(() => {
    refresh()
    return CustomAssetManager.subscribe(refresh)
  }, [refresh])

  // A save KEEPS the row selected. Clearing the selection threw the author
  // back to the empty state after every edit, so composing a gesture meant
  // re-finding it in the list between each change (Florent, 2026-09-15).
  //
  // The row we select is the one the SERVER returned, so a brand-new gesture
  // becomes the selected existing gesture — the header flips to Edit and the
  // Test / Delete buttons appear — and an edit re-seats the saved values.
  // Deleting still clears: that row is gone, so there is nothing to keep.
  //
  // A save that returns no id leaves the selection alone rather than dropping
  // it: losing your place is the failure being fixed, and it must not come
  // back as the way an unexpected response is handled.
  const handleSaveGesture = async (id, form) => {
    const row = id
      ? await CustomAssetManager.updateGesture(id, form)
      : await CustomAssetManager.createGesture(form)
    if (row?.id) setSelected({ kind: 'gesture', data: row })
  }

  const handleDeleteGesture = async (id) => {
    await CustomAssetManager.deleteGesture(id)
    setSelected(null)
  }

  // Same rule as handleSaveGesture above.
  const handleSavePrefab = async (id, form) => {
    const row = id
      ? await CustomAssetManager.updatePrefab(id, form)
      : await CustomAssetManager.createPrefab(form)
    if (row?.id) setSelected({ kind: 'prefab', data: row })
  }

  const handleDeletePrefab = async (id) => {
    await CustomAssetManager.deletePrefab(id)
    setSelected(null)
  }

  const renderDetail = () => {
    if (!selected) return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: FG3, fontSize: 11, fontFamily: 'var(--m-font)', flexDirection: 'column', gap: 8 }}>
        <div>Select an item or click + to create</div>
      </div>
    )
    if (selected.kind === 'gesture' || selected.kind === 'newGesture')
      return <GestureDetail gesture={selected.kind === 'newGesture' ? null : selected.data} animAssets={animAssets} onSave={handleSaveGesture} onDelete={handleDeleteGesture} />
    if (selected.kind === 'prefab' || selected.kind === 'newPrefab')
      return <PrefabDetail prefab={selected.kind === 'newPrefab' ? null : selected.data} animAssets={animAssets} audioAssets={audioAssets} onSave={handleSavePrefab} onDelete={handleDeletePrefab} />
    return null
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', background: BG, fontFamily: 'var(--m-font)', color: FG, overflow: 'hidden' }}>

      {/* Left panel */}
      <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${LINE}`, background: BG2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ flexShrink: 0, background: BG3, borderBottom: `1px solid ${LINE}` }}>
          <div style={{ height: 32, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
            <span style={{ fontFamily: 'var(--m-font)', fontSize: 10, letterSpacing: '.18em', color: ACCENT + 'cc', fontWeight: 500, textTransform: 'uppercase' }}>Composer</span>
          </div>
        </div>

        {/* Composer list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <SectionHeader label="Gestures" count={gestures.length} onAdd={() => setSelected({ kind: 'newGesture', data: null })} />
          {gestures.map(g => (
            <ListItem key={g.id} label={g.display_name || g.tag} sub={g.tag !== (g.display_name || g.tag) ? g.tag : undefined}
              selected={selected?.data?.id === g.id && selected?.kind === 'gesture'}
              onClick={() => setSelected({ kind: 'gesture', data: g })}
              onAction={() => testGesture(g)} actionLabel="▶"
            />
          ))}
          <SectionHeader label="Prefabs" count={prefabs.length} onAdd={() => setSelected({ kind: 'newPrefab', data: null })} />
          {prefabs.map(p => (
            <ListItem key={p.id} label={p.display_name || p.tag}
              sub={p.tag !== (p.display_name || p.tag) ? p.tag : undefined}
              selected={selected?.data?.id === p.id && selected?.kind === 'prefab'}
              onClick={() => setSelected({ kind: 'prefab', data: p })}
              badge={p.status} badgeColor={statusColor(p.status)}
              onAction={() => testPrefab(p)} actionLabel="▶"
            />
          ))}
        </div>
      </div>

      {/* Right: detail */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {renderDetail()}
      </div>

    </div>
  )
}
